import {React} from 'lib';
import Quagga, {QuaggaJSResultObject, MediaTrackConstraintsWithDeprecated, QuaggaJSConfigObject, QuaggaJSReaderConfig} from '@ericblade/quagga2';


function getMedianOfCodeErrors(decodedCodes: QuaggaJSResultObject['codeResult']['decodedCodes']): number {
    const errors = decodedCodes.filter(x => x.error !== undefined).map(x => x.error as number);
    errors.sort((a, b) => a - b);
    const half = Math.floor(errors.length / 2);
    if (errors.length % 2 === 1) {
        return errors[half];
    }
    return (errors[half - 1] + errors[half]) / 2;
}

const defaultConstraints: MediaTrackConstraintsWithDeprecated = {
    width: {ideal: 1280},
    height: {ideal: 720},
    facingMode: 'environment',
    advanced: [{focusMode: 'continuous'} as any],
};

const defaultLocatorSettings = {
    patchSize: 'medium',
    halfSample: true,
    debug: {
        showCanvas: false,
    },
};

const defaultDecoders = ['ean_reader', 'ean_8_reader'];


export const Scanner = ({
    onDetected,
    scannerRef,
    onScannerReady,
    constraints = defaultConstraints,
    locator = defaultLocatorSettings,
    numOfWorkers = navigator.hardwareConcurrency || 0,
    decoders = defaultDecoders,
    locate = true,
}: {
    onDetected(code: string): void;
    scannerRef: React.RefObject<HTMLDivElement | null>;
    onScannerReady?(): void;
    constraints?: MediaTrackConstraintsWithDeprecated;
    locator?: QuaggaJSConfigObject['locator'];
    numOfWorkers?: number;
    decoders?: (QuaggaJSReaderConfig | string)[];
    locate?: boolean;
}) => {
    const detected = React.useRef<{[key: string]: number}>({});
    const holding = React.useRef(false);
    const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const errorCheck = React.useCallback((result: QuaggaJSResultObject) => {
        if (holding.current) {
            return;
        }
        const err = getMedianOfCodeErrors(result.codeResult.decodedCodes);
        const code = result.codeResult.code;
        if (err < 0.5 && code) {
            if (code in detected.current) {
                detected.current[code] += 1;
                if (detected.current[code] >= 3) {
                    detected.current = {};
                    holding.current = true;
                    holdTimer.current = setTimeout(() => {
                        holding.current = false;
                        holdTimer.current = null;
                        onDetected(code);
                    }, 300);
                }
            } else {
                detected.current[code] = 1;
            }
        }
    }, [onDetected]);

    const drawOverlay = React.useCallback((result: QuaggaJSResultObject | undefined) => {
        if (holding.current) {
            return;
        }
        const ctx = Quagga.canvas.ctx.overlay;
        const canvas = Quagga.canvas.dom.overlay;
        if (!ctx || !canvas) {
            return;
        }
        const w = parseInt(canvas.getAttribute('width') ?? '0', 10);
        const h = parseInt(canvas.getAttribute('height') ?? '0', 10);
        ctx.clearRect(0, 0, w, h);
        if (!result) {
            return;
        }
        if (result.codeResult && result.codeResult.code && result.line) {
            Quagga.ImageDebug.drawPath(result.line, {x: 'x', y: 'y'}, ctx,
                {color: 'red', lineWidth: 3});
        }
    }, []);

    React.useEffect(() => {
        Quagga.init({
            inputStream: {
                type: 'LiveStream',
                constraints,
                target: scannerRef.current ?? undefined,
            },
            locator,
            numOfWorkers,
            decoder: { readers: decoders },
            locate,
        }, (err) => {
            if (err) {
                return console.log('Error starting Quagga:', err);
            }
            if (scannerRef?.current) {
                Quagga.start();
                if (onScannerReady) {
                    onScannerReady();
                }
            }
        });
        Quagga.onDetected(errorCheck);
        Quagga.onProcessed(drawOverlay);
        return () => {
            Quagga.offDetected(errorCheck);
            Quagga.offProcessed(drawOverlay);
            Quagga.stop();
            if (holdTimer.current) {
                clearTimeout(holdTimer.current);
                holdTimer.current = null;
            }
            holding.current = false;
        };
    }, [onDetected, onScannerReady, scannerRef, errorCheck, drawOverlay, constraints, locator, numOfWorkers, decoders, locate]);
    return null;
}

